import {BeneficiariesPaperwork, BeneficiariesPaperworkStatus, Beneficiary} from 'lib/entities';
import {HttpError} from 'lib/server/errors';
import {put, select, takeLatest} from 'redux-saga/effects';
import * as actions from 'redux/actions/beneficiariesActions';
import {ActionType} from 'redux/actions/beneficiariesActionTypes';
import {BeneficiaryDuplicateNames, BeneficiaryIsFinanciallyDependFalse} from 'redux/errors/BeneficiaryDuplicateNames';
import {MaxBeneficiariesExceededError} from 'redux/errors/MaxBeneficiariesExceededError';
import {MaxPayoutPercentageExceededError} from 'redux/errors/MaxPayoutPercentageExceededError';
import {BeneficiariesState, BeneficiaryState, MAX_BENEFICIARIES} from 'redux/reducers/beneficiariesReducer';

type AppState = {
    beneficiaries: BeneficiariesState;
};

async function saveBeneficiariesPaperworkAPI(
    paperwork: Partial<BeneficiariesPaperwork>,
): Promise<BeneficiariesPaperwork | null> {
    const resp = await fetch('/api/beneficiaries/', {
        method: 'PATCH',
        body: JSON.stringify(paperwork),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    let data;

    try {
        data = await resp.json();
    } catch (e) {
        data = {};
    }

    if (resp.status === 200) return data;
    throw new HttpError(resp.status, data.message || 'Unknown error');
}

// This is a base saga that is used within other sagas and as such should not be
// used as its own due to re-throwing on error.
function* saveBeneficiariesPaperwork({payload}: Action<Partial<BeneficiariesPaperwork>>) {
    try {
        const paperwork: BeneficiariesPaperwork = yield saveBeneficiariesPaperworkAPI(payload);
        yield put(actions.saveBeneficiariesPaperworkSuccess(paperwork));
    } catch (e) {
        yield put(actions.saveBeneficiariesPaperworkFailed(payload, e as Error));
        throw new Error('Could not save beneficiaries paperwork.');
    }
}

export function validateBeneficiaries(beneficiaries: Record<string, BeneficiaryState>): {
    hasDuplicate: boolean;
    isFinanciallyDependFalse: boolean;
    hasNoPayoutOfHundredPercent: boolean;
} {
    const beneficiaryList = Object.values(beneficiaries);
    const beneficiariesMap = new Map();
    let emptyBeneficiaryForms = 0;
    let total = 0;

    beneficiaryList.forEach(({beneficiary}) => {
        const name = `${beneficiary.firstName.trim().toLowerCase()}-${beneficiary.lastName.trim().toLowerCase()}`;
        const dob = beneficiary.dateOfBirth.trim();

        total += beneficiary.payoutPercentage;
        if (`${name}${dob}`.length > 1) {
            beneficiariesMap.set(`${name}${dob}`, `${name}${dob}`);
        } else {
            emptyBeneficiaryForms++;
        }
    });
    const hasDuplicate = beneficiariesMap.size + emptyBeneficiaryForms !== beneficiaryList.length;
    const isFinanciallyDependFalse = beneficiaryList.some(({beneficiary}) => beneficiary.isFinanciallyDepend === false);
    return {
        hasDuplicate,
        isFinanciallyDependFalse,
        hasNoPayoutOfHundredPercent: total !== 100,
    };
}

function* saveBeneficiary({payload, meta}: Action<Beneficiary>) {
    try {
        const beneficiariesState: BeneficiariesState = yield select(({beneficiaries}: AppState) => beneficiaries);
        const beneficiaryState = beneficiariesState.beneficiaries[payload.id];
        const validatedResult = validateBeneficiaries(beneficiariesState.beneficiaries);

        if (validatedResult.hasDuplicate) throw new BeneficiaryDuplicateNames();
        if (validatedResult.isFinanciallyDependFalse) throw new BeneficiaryIsFinanciallyDependFalse();
        if (
            !beneficiaryState.isSaved &&
            Object.values(beneficiariesState.beneficiaries).filter((b) => b.isSaved).length === MAX_BENEFICIARIES
        )
            throw new MaxBeneficiariesExceededError();

        const updatedBeneficiaries = <Record<string, Beneficiary>>{};
        // eslint-disable-next-line no-restricted-syntax, guard-for-in
        for (const i in beneficiariesState.beneficiaries)
            updatedBeneficiaries[i] = beneficiariesState.beneficiaries[i].beneficiary;
        const completedPaperwork = {
            ...beneficiariesState.paperwork?.completedPaperwork,
            [payload.id]: payload,
        };
        const percentageTotal = Object.values(completedPaperwork)
            .filter((b) => !b.contingent)
            .reduce((carry, b) => b.payoutPercentage + carry, 0);
        if (percentageTotal > 100) throw new MaxPayoutPercentageExceededError();
        yield saveBeneficiariesPaperwork(
            actions.saveBeneficiariesPaperwork({
                ...beneficiariesState.paperwork,
                completedPaperwork: JSON.stringify({...completedPaperwork, ...updatedBeneficiaries}),
                status: BeneficiariesPaperworkStatus.INCOMPLETE,
            }),
        );

        yield put(actions.saveBeneficiarySuccess(payload, meta));
    } catch (e) {
        console.error(e);
        yield put(actions.saveBeneficiaryFailed(payload, e as Error));
    }
}

function* removeBeneficiary({payload}: Action<string>) {
    try {
        const beneficiariesState: BeneficiariesState = yield select(({beneficiaries}: AppState) => beneficiaries);

        const {[payload]: removedBeneficiary, ...completedPaperwork} =
            beneficiariesState.paperwork?.completedPaperwork || {};

        yield saveBeneficiariesPaperwork(
            actions.saveBeneficiariesPaperwork({
                ...beneficiariesState.paperwork,
                completedPaperwork: JSON.stringify(completedPaperwork),
                status: BeneficiariesPaperworkStatus.INCOMPLETE,
            }),
        );

        yield put(actions.removeBeneficiarySuccess(payload));
    } catch (e) {
        yield put(actions.removeBeneficiaryFailed(e as Error));
    }
}

function* confirmBeneficiaries() {
    try {
        const paperwork: BeneficiariesPaperwork = yield select(({beneficiaries}: AppState) => beneficiaries.paperwork);
        const updatedPaperwork = {
            ...paperwork,
            completedPaperwork: JSON.stringify(paperwork.completedPaperwork),
            status: BeneficiariesPaperworkStatus.CONFIRMED,
        };
        yield saveBeneficiariesPaperwork(actions.saveBeneficiariesPaperwork(updatedPaperwork));
        yield put(actions.confirmBeneficiariesSuccess(updatedPaperwork));
    } catch (e) {
        yield put(actions.confirmBeneficiariesFailed(e as Error));
    }
}

async function startBeneficiariesAPI() {
    const resp = await fetch('/api/beneficiaries/', {
        method: 'POST',
    });

    if (resp.status === 200) {
        const paperwork = await resp.json();
        return paperwork;
    }

    return null;
}

function* startBeneficiaries() {
    try {
        const paperwork: BeneficiariesPaperwork = yield startBeneficiariesAPI();
        yield put(actions.startBeneficiariesSuccess(paperwork));
    } catch (e) {
        yield put(actions.startBeneficiariesFailed(e as Error));
    }
}

export default function* beneficiariesSaga() {
    yield takeLatest(ActionType.BENEFICIARIES_START, startBeneficiaries);
    yield takeLatest(ActionType.BENEFICIARIES_SAVE, saveBeneficiary);
    yield takeLatest(ActionType.BENEFICIARIES_REMOVE, removeBeneficiary);
    yield takeLatest(ActionType.BENEFICIARIES_CONFIRM, confirmBeneficiaries);
}
